<template>
  <v-container :class="['gym-availability-grid', {'sm': $vuetify.breakpoint.smAndDown}]">
    <v-row no-gutters>
      <v-col class="availability-grid-legend-col" cols="12" md="6">
        <div class="availability-grid-legend"
             style="background-color: rgb(178,216,210);"><label></label>
        </div>
        <div class="availability-grid-legend-text"><label>{{ $t('gym_booking.available') }}</label></div>
      </v-col>
      <v-col class="availability-grid-legend-col" cols="12" md="3">
        <div class="availability-grid-legend"
             style="background-color: rgb(225,184,93);"><label></label>
        </div>
        <div class="availability-grid-legend-text"><label>{{ $t('gym_booking.almost_full') }}</label></div>
      </v-col>
      <v-col class="availability-grid-legend-col" cols="12" md="3">
        <div class="availability-grid-legend"
             style="background-color: red;"><label></label>
        </div>
        <div class="availability-grid-legend-text"><label>{{ $t('gym_booking.full') }}</label></div>
      </v-col>
      <v-col class="availability-grid-legend-col" cols="12" md="6">
        <div class="availability-grid-legend"
             style="background-color: rgb(200,200,200);"><label></label>
        </div>
        <div class="availability-grid-legend-text"><label>{{ $t('gym_booking.booked_already') }}</label></div>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-calendar type="custom-daily"
                    :start="fromDate"
                    :end="toDate"
                    :events="sessions"
                    :first-interval="1"
                    :interval-count="1"
                    :interval-height="64"
                    :interval-width="0"
                    :interval-format="()=> ''"
                    event-color="white"
                    @click:event="sessionClicked"
                    :locale="$i18n.locale"
                    :class="{'ie-fix': isIE}">
          <template v-slot:event="{event}">
            <label :class="['availability-cell-label', {'selected': isSessionSelected(event)}]">
              <!--              {{ priceTierMap[event.price_tier].name.substr(0, 1).toUpperCase() }}-->
              {{ Math.min(event.count, gymBookingMaxCount + event.offset) }} <span class="max-count">/ {{ gymBookingMaxCount + event.offset }}</span>
            </label>
            <div class="availability-cell-border" :style="getCellBorderStyle(event)"></div>
          </template>
        </v-calendar>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {DateTime} from 'luxon'
import {computed, onMounted, ref, watch} from "@vue/composition-api";

export default {
  name: 'GymAvailabilityGrid',
  props: {
    typeId: String,
    fromDate: String,
    toDate: String,
    maxDailySession: Number,
  },
  setup(props, {root, emit}) {
    const isIE = computed(() => {
      let userAgent = window.navigator.userAgent;
      let msie = userAgent.indexOf("MSIE ");

      if (msie > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./) || /Edge/.test(navigator.userAgent))  // If Internet Explorer, return version number
      {
        return true;
      } else {
        return false;
      }
    });

    const sessions = ref([]);
    const gymBookingMaxCount = ref(1);
    const bookedDates = ref([]);
    const selectedDate = ref(null);
    const getSessions = function () {
      root.$callAPI('aafb/gym_booking/status/', {today: false}, (response) => {
        gymBookingMaxCount.value = response.body.max;
        bookedDates.value = response.body.booked_dates.map((d) => DateTime.fromISO(d).toFormat('yyyy-MM-dd 01:00'));

        let countMap = {};

        let d = DateTime.fromISO(props.fromDate);
        let end = DateTime.fromISO(props.toDate);
        while (d.startOf('day') <= end.endOf('day')) {
          countMap[d.toFormat('yyyy-MM-dd')] = 0;

          d = d.plus({day: 1});
        }

        const today = DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd');
        response.body.counts.forEach((c) => {
          let date = DateTime.fromISO(c.date).toFormat('yyyy-MM-dd');

          countMap[date] = c.count;
        })

        sessions.value = Object.keys(countMap).map((d) => {
          return {
            name: d,
            count: countMap[d],
            offset: d === today ? response.body.today_offset : 0,
            start: `${d} 01:00`,
            end: `${d} 02:00`,
          }
        })
      });
    };
    const getCellBorderStyle = function (event) {
      const selected = isSessionSelected(event);
      let color;

      let gymBookingAvailableCount = gymBookingMaxCount.value - event.count;
      let gymBookingPercentage = Math.floor((gymBookingMaxCount.value - gymBookingAvailableCount) / gymBookingMaxCount.value * 100)

      if (bookedDates.value.indexOf(event.start) !== -1) {
        color = '200,200,200';
      } else if (gymBookingAvailableCount === 0) {
        color = '255,0,0';
      } else if (gymBookingPercentage >= 75) {
        color = '225,184,93';
      } else {
        color = '178,216,210';
      }

      return {
        'border': `solid ${selected ? 5 : 3}px rgb(${color})`,
        'background-color': selected ? `rgba(${color}, 0.2)` : 'transparent',
        height: '100%',
        width: '100%',
      }
    };

    const isSessionSelected = function (event) {
      let eventDate = DateTime.fromFormat(event.start, 'yyyy-MM-dd HH:mm').toFormat('yyyy-MM-dd');

      return eventDate === selectedDate.value;
    }
    const sessionClicked = function ({event}) {
      if (event.count === gymBookingMaxCount.value) return;

      if (bookedDates.value.indexOf(event.start) !== -1) return;

      selectedDate.value = DateTime.fromFormat(event.start, 'yyyy-MM-dd HH:mm').toFormat('yyyy-MM-dd')

      emit('input', selectedDate.value);
    }

    watch([() => props.fromDate, () => props.toDate], () => {
      getSessions();
    })

    onMounted(() => {
      getSessions();
    })

    return {
      isIE,
      bookedDates,
      sessions,
      getCellBorderStyle,
      gymBookingMaxCount,
      isSessionSelected,
      sessionClicked,
    }
  },
}
</script>

<style lang="less">
.ie-fix {
  .v-calendar-daily__body {
    flex-basis: auto !important;
  }
}

.gym-availability-grid {
  text-align: center;
  padding: 0;
  text-transform: uppercase;

  .availability-grid-legend-col {
    text-align: left;
  }

  .availability-grid-legend {
    display: inline-block;
    width: 45px;
    height: 45px;
    /*max-width: 11vw;*/
    max-height: 11vw;
    /*line-height: 11vw;*/
    color: white;

    position: relative;
    padding: 0;

    label {
      margin: 0;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      @media only screen and (max-width: 600px) {
        font-size: 0.9rem;
      }
    }
  }

  .availability-grid-legend-text {
    display: inline-block;
    height: 45px;
    max-height: 11vw;
    /*line-height: 11vw;*/

    position: relative;
    padding: 0;

    label {
      margin: 0;
      padding-left: 8px;
      //position: absolute;
      top: 50%;
      left: 0%;
      text-overflow: unset;
      white-space: nowrap;
      transform: translate(0%, -50%);

      @media only screen and (max-width: 600px) {
        font-size: 0.9rem;
      }
    }
  }

  .v-calendar {
    border-top: none !important;
    border-left: none !important;

    .v-calendar-daily__day-interval {
      border-top: none !important;

      &:last-child {
        display: none;
      }
    }

    .v-calendar-daily__interval:first-child {
      &:after {
        border-top: none !important;
      }
    }

    .v-calendar-daily__interval:last-child {
      &:after {
        border-bottom: none !important;
      }
    }

    .v-calendar-daily__interval-text {
      top: -3px !important;
      font-size: 8px !important;
    }

    .v-calendar-daily_head-day {
      border-bottom: none !important;

      &:last-child {
        border-right: none !important;
      }
    }

    .v-calendar-daily__intervals-body {
      border-right: none !important;
    }

    .v-calendar-daily__day {
      border-right: none !important;
      border-bottom: none !important;

      &:last-child {
        border-right: none !important;
      }
    }

    .v-event-timed-container {
      margin-right: 0 !important;
    }

    .v-event-timed {
      border-radius: 0;
    }

    .availability-cell-label {
      font-size: 16px;
      user-select: none;
      margin: 0;
      position: absolute;
      top: 50%;
      left: 50%;
      -webkit-transform: translate(-50%, -50%);
      transform: translate(-50%, -50%);
      color: rgba(0, 0, 0, 0.87);
      cursor: pointer;

      &.selected {
        font-weight: bold;
      }

      .max-count {
        font-size: 0.7rem;
      }
    }
  }

  .v-btn--fab.v-size--default {
    height: initial;
    width: initial;
  }

  &.sm {
    .availability-grid-legend-col {

    }
  }
}
</style>
